import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../context/I18nContext";
import { Calendar, Tooth, Receipt, ArrowRight, ChatCircleDots } from "@phosphor-icons/react";

export default function PortalDashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const headingFamily = lang === "hi" ? "Hind, Manrope, sans-serif" : "Manrope, sans-serif";
  const [appts, setAppts] = useState([]);
  const [tx, setTx] = useState([]);
  const [inv, setInv] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/portal/appointments"),
      api.get("/portal/treatments"),
      api.get("/portal/invoices"),
    ]).then(([a, t, i]) => {
      setAppts(a.data);
      setTx(t.data);
      setInv(i.data);
    });
  }, []);

  const today = new Date().toISOString().slice(0,10);
  const upcoming = appts.find(a => a.starts_at >= today);
  const outstanding = inv.reduce((s, i) => s + (i.balance || 0), 0);

  return (
    <div data-testid="portal-dashboard">
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">{lang==="hi"?"à¤®à¤°à¥€à¤œà¤¼ à¤ªà¥‹à¤°à¥à¤Ÿà¤²":"Patient portal"}</div>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-zinc-900" style={{fontFamily:headingFamily}}>
        {t("portal.greeting")}, {user?.name?.split(" ")[0] || "there"}.
      </h1>
      <p className="text-sm text-zinc-500 mt-2">{t("portal.subtitle")}</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label={t("portal.next_visit")} value={upcoming ? new Date(upcoming.starts_at).toLocaleString() : t("portal.not_scheduled")} icon={Calendar} headingFamily={headingFamily}/>
        <Stat label={t("portal.treatments")} value={tx.length} icon={Tooth} headingFamily={headingFamily}/>
        <Stat label={t("portal.outstanding")} value={`â‚¹${outstanding.toFixed(0)}`} icon={Receipt} accent={outstanding>0} headingFamily={headingFamily}/>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/portal/book" className="group bg-[#E60000] hover:bg-[#CC0000] text-white rounded-sm p-6 transition-colors">
          <Calendar size={28} weight="duotone" color="#ffffff"/>
          <div className="mt-3 text-xl font-bold tracking-tight" style={{fontFamily:headingFamily}}>{t("portal.book_card_title")}</div>
          <div className="text-sm text-white/80 mt-1">{t("portal.book_card_body")}</div>
          <div className="mt-4 text-xs uppercase tracking-[0.25em] font-bold inline-flex items-center gap-1">
            {t("portal.book_card_cta")} <ArrowRight size={12} weight="bold"/>
          </div>
        </Link>
        <Link to="/portal/appointments" className="group bg-white border border-zinc-200 rounded-sm p-6 hover:border-[#E60000] transition-colors">
          <Calendar size={28} weight="duotone" color="#E60000"/>
          <div className="mt-3 text-xl font-bold tracking-tight" style={{fontFamily:headingFamily}}>{t("portal.appts_card_title")}</div>
          <div className="text-sm text-zinc-500 mt-1">{t("portal.appts_card_body")}</div>
          <div className="mt-4 text-xs uppercase tracking-[0.25em] font-bold text-zinc-700 inline-flex items-center gap-1 group-hover:text-[#E60000]">
            {t("portal.view")} <ArrowRight size={12} weight="bold"/>
          </div>
        </Link>
        <Link to="/portal/history" className="group bg-white border border-zinc-200 rounded-sm p-6 hover:border-[#E60000] transition-colors">
          <Tooth size={28} weight="duotone" color="#E60000"/>
          <div className="mt-3 text-xl font-bold tracking-tight" style={{fontFamily:headingFamily}}>{t("portal.history_card_title")}</div>
          <div className="text-sm text-zinc-500 mt-1">{t("portal.history_card_body")}</div>
          <div className="mt-4 text-xs uppercase tracking-[0.25em] font-bold text-zinc-700 inline-flex items-center gap-1 group-hover:text-[#E60000]">
            {t("portal.open")} <ArrowRight size={12} weight="bold"/>
          </div>
        </Link>
        <Link to="/portal/invoices" className="group bg-white border border-zinc-200 rounded-sm p-6 hover:border-[#E60000] transition-colors">
          <Receipt size={28} weight="duotone" color="#E60000"/>
          <div className="mt-3 text-xl font-bold tracking-tight" style={{fontFamily:headingFamily}}>{t("portal.invoices_card_title")}</div>
          <div className="text-sm text-zinc-500 mt-1">{t("portal.invoices_card_body")}</div>
          <div className="mt-4 text-xs uppercase tracking-[0.25em] font-bold text-zinc-700 inline-flex items-center gap-1 group-hover:text-[#E60000]">
            {t("portal.view")} <ArrowRight size={12} weight="bold"/>
          </div>
        </Link>
        <Link to="/portal/enquiry" className="group bg-[#0A0A0A] text-white rounded-sm p-6 hover:bg-zinc-800 transition-colors">
          <ChatCircleDots size={28} weight="duotone" color="#E60000"/>
          <div className="mt-3 text-xl font-bold tracking-tight" style={{fontFamily:headingFamily}}>{t("portal.enquiry_card_title")}</div>
          <div className="text-sm text-zinc-400 mt-1">{t("portal.enquiry_card_body")}</div>
          <div className="mt-4 text-xs uppercase tracking-[0.25em] font-bold text-zinc-400 inline-flex items-center gap-1 group-hover:text-[#E60000]">
            {t("portal.start")} <ArrowRight size={12} weight="bold"/>
          </div>
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, headingFamily }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        <Icon size={14} weight="bold"/> {label}
      </div>
      <div className={`mt-2 text-2xl font-black tracking-tighter ${accent ? "text-[#E60000]" : "text-zinc-900"}`} style={{fontFamily:headingFamily}}>
        {value}
      </div>
    </div>
  );
}
